/**
 * Role Registry — singleton that holds role-specific UI configuration.
 *
 * After auth resolves, app.js dynamically imports one role module
 * (user.js / head.js / admin.js). That module calls registry.register(...)
 * to declare nav items, routes, dashboard widgets, CDR config, and header actions.
 *
 * Router, sidebar, dashboard, CDR, and header consume the registry.
 */

interface NavItem {
    section?: string;
    icon?: string;
    label?: string;
    page?: string;
    href?: string;
}

interface Route {
    loader: () => Promise<any>;
    role?: string;
}

interface CdrConfig {
    showExtensionFilter: boolean;
    showDeptFilter: boolean;
    showTrunkColumn: boolean;
    showRecordingPlayer: boolean;
}

interface HeaderAction {
    id: string;
    label: string;
    href?: string;
}

interface RegistryState {
    role: string;
    navItems: NavItem[];
    routes: Record<string, Route>;
    pageTitles: Record<string, string>;
    dashboardWidgets: string[];
    cdrConfig: CdrConfig;
    headerActions: HeaderAction[];
    forwardingScope: 'own' | 'dept' | 'all';
    deptCanToggle: boolean;
}

type RegistryConfig = Partial<RegistryState>;

const _state: RegistryState = {
    role: 'user',
    navItems: [],
    routes: {},
    pageTitles: {},
    dashboardWidgets: [],
    cdrConfig: {
        showExtensionFilter: false,
        showDeptFilter: false,
        showTrunkColumn: true,
        showRecordingPlayer: true,
    },
    headerActions: [],
    forwardingScope: 'own',
    deptCanToggle: false,
};

export const registry = {
    /**
     * Called once by the active role module to populate the registry.
     */
    register(config: RegistryConfig): void {
        Object.assign(_state, config);
    },

    getRole(): string {
        return _state.role;
    },
    getNavItems(): NavItem[] {
        return _state.navItems;
    },
    getRoutes(): Record<string, Route> {
        return _state.routes;
    },
    getPageTitles(): Record<string, string> {
        return _state.pageTitles;
    },
    getDashboardWidgets(): string[] {
        return _state.dashboardWidgets;
    },
    getCdrConfig(): CdrConfig {
        return _state.cdrConfig;
    },
    getHeaderActions(): HeaderAction[] {
        return _state.headerActions;
    },
    getForwardingScope(): 'own' | 'dept' | 'all' {
        return _state.forwardingScope;
    },
    canToggleDeptLines(): boolean {
        return _state.deptCanToggle;
    },
};
